'use client'

import ChevronLeft from '@/components/icons/chevron-left'
import ChevronRightIcon from '@/components/icons/chevron-right'
import Image from 'next/image'
import Link from 'next/link'
import { useMemo, useRef } from 'react'

const _TITLES = {
  driver: 'Race in',
  commentator: 'Commentate for',
  livery: 'Design For',
}

const CARD_WIDTH = 220

export default function GamesSection({
  type,
  games = [
    'acc',
    'f1-2023',
    'iracing',
    'rfactor-2',
    'assetto-corsa',
    'automobilista',
    'automobilista-2',
    'dirt-rally-20',
    'gran-turismo-7',
  ],
  user,
}) {
  const containerRef = useRef()

  const handleScroll = (scrollAmount) => {
    const newScrollPosition = containerRef.current.scrollLeft + scrollAmount

    containerRef.current.scrollLeft = newScrollPosition
  }

  const Game = ({ game }) => {
    return (
      <div className="flex flex-col rounded pr-[20px]">
        <Link
          href={'/sim-racing-games/' + game.name}
          className="z-1 relative w-[200px] rounded-[12px] bg-neutral-blue-800 pt-[143%]"
        >
          <Image
            src={
              game.cardImgUrl && game.cardImgUrl.length > 0
                ? game.cardImgUrl
                : '/img/default/grid.png'
            }
            alt="An image of the livery designer"
            objectFit="cover"
            fill
            className="w-[calc(100%)]! left-0 top-0 h-full rounded-[12px]"
          />
        </Link>

        <div className="mt-4 flex w-[200px] flex-col">
          <Link
            href={'/sim-racing-games/' + game.name}
            className="flex flex-col"
          >
            <p className="text-wrap font-inter font-semibold text-shades-0">
              {game.longname || game.nicename}
            </p>
          </Link>
        </div>
      </div>
    )
  }

  const uniqueGames = useMemo(() => {
    // First, flatten/normalize the games array.
    // If a game is nested under 'game' (broadcaster/commentator), extract it.
    const normalizedGames = games.map((game) => (game.game ? game.game : game))

    // Then deduplicate based on the game name
    const uniqueNames = Array.from(new Set(normalizedGames.map((g) => g.name)))

    return uniqueNames.map((name) =>
      normalizedGames.find((g) => g.name === name),
    )
  }, [games])

  return (
    <div className="flex w-full flex-col gap-8 rounded bg-neutral-blue-700 p-8 xl:gap-10">
      <div className="flex justify-between">
        <p className="text-center font-syncopate text-xl font-bold uppercase text-shades-0 xl:text-start xl:text-2xl">
          {user.username}
          's Games
        </p>
        <div className="flex self-center">
          {uniqueGames.length > 3 && (
            <>
              <button
                className="h-fit rounded-full border border-neutral-blue-500 bg-neutral-blue-500 p-[7px] hover:border-neutral-blue-400 hover:bg-neutral-blue-400"
                onClick={() => {
                  handleScroll(-CARD_WIDTH)
                }}
              >
                <ChevronLeft className="w-[20px] stroke-shades-0" />
              </button>
              <button
                className="ml-[18px] h-fit rounded-full border border-neutral-blue-500 bg-neutral-blue-500 p-[7px] hover:border-neutral-blue-400 hover:bg-neutral-blue-400"
                onClick={() => {
                  handleScroll(CARD_WIDTH)
                }}
              >
                <ChevronRightIcon className="w-[20px] stroke-shades-0" />
              </button>
            </>
          )}
        </div>
      </div>
      <div
        ref={containerRef}
        className="flex w-full flex-col gap-4 overflow-x-hidden scroll-smooth rounded desktop-sm:gap-10"
      >
        {uniqueGames.length > 0 ? (
          <div className="flex w-fit gap-3">
            {uniqueGames.map((game, index) => (
              <Game game={game} key={`Game${game.name}-${index}`} />
            ))}
          </div>
        ) : (
          <div className="grid flex-1 text-center">
            <p className="flex h-36 items-center justify-center text-center text-xl text-shades-0">
              No games listed yet
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
